import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import dayjs from "dayjs";
import { Helmet } from "react-helmet-async";
import { base_URL, frontend_URL } from "../../config/config";
import toast from "react-hot-toast";
import { FaArrowLeft } from "react-icons/fa";
import { buildArticleSchema } from "../../utils/articleSchema";
import ArticleSchema from "../../components/ArticleSchema";

export default function BlogDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const blogId = id;
  const [blog, setBlog] = useState();
  const [htmlContent, setHtmlContent] = useState("");
  const [schema, setSchema] = useState(null);
  const blogUrl = `${frontend_URL}/blogs/${blogId}`;

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const res = await axios.get(`${base_URL}/api/blogs/getBlog/${blogId}`);
        setBlog(res.data);

        if (res.data.content) {
          const htmlRes = await axios.post(
            `${base_URL}/api/blogs/getHtmlFromS3`,
            {
              fileName: res.data.content,
            },
          );

          const { body } = htmlRes.data;
          if (!body) throw new Error("No body content found in S3 response");

          const sanitizedHtml = DOMPurify.sanitize(body).replace(
            /<a /g,
            '<a target="_blank" rel="noopener noreferrer" ',
          );

          setHtmlContent(sanitizedHtml);

          setSchema(
            buildArticleSchema({
              url: blogUrl,
              title: res.data?.seoTitle,
              description: res.data?.seoDescription,
              image: res.data?.heroImageUrl,
              publishedAt: res.data?.createdAt,
              updatedAt: res.data?.updatedAt,
            }),
          );
        }
      } catch (err) {
        console.error("Error fetching blog:", err);
        toast.error("Failed to load blog content");
      }
    };

    fetchBlog();
  }, [blogId]);

  // console.log("BLOG URL", blogUrl);

  // const schema = buildArticleSchema({
  // 	url: blogUrl,
  // 	title: blog?.title,
  // 	description: blog?.metaDescription || blog?.excerpt,
  // 	image: blog?.heroImageUrl,
  // 	publishedAt: blog?.createdAt,
  // 	updatedAt: blog?.updatedAt,
  // });

  if (typeof blog === "undefined") {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center text-gray-400 text-lg animate-pulse">
          Loading blog...
        </div>
      </div>
    );
  }

  if (!blog || !blog.title) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center text-red-400 text-lg">Blog not found.</div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <link rel="canonical" href={blogUrl} />
      </Helmet>
      <ArticleSchema schema={schema} />
      <div className="max-w-5xl mx-auto px-4 py-10">
        {/* Back Button */}
        <div className="mb-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
          >
            <FaArrowLeft /> Back
          </button>
        </div>

        <div className="bg-white rounded-xl p-6 md:p-10">
          <h1 className="text-3xl md:text-4xl font-bold mb-2 text-gray-900 leading-tight">
            {blog.title}
          </h1>

          <div className="flex items-center text-sm text-gray-500 mb-6 gap-2">
            <span>{dayjs(blog.updatedAt).format("MMMM D, YYYY")}</span>
          </div>

          {blog.heroImageUrl && (
            <img
              src={blog.heroImageUrl}
              alt={blog.title}
              className="w-full h-64 object-cover rounded-lg shadow mb-8 transition-transform duration-200 hover:scale-105"
              loading="lazy"
            />
          )}

          <div
            className="prose prose-2xl max-w-none prose-headings:text-gray-900 prose-p:text-gray-800 prose-img:rounded-lg prose-img:shadow mt-10 leading-relaxed space-y-6"
            style={{ fontSize: "1.25rem" }}
          >
            <style>
              {`
				.prose a {
					color: #2563eb; /* Tailwind blue-600 */
					
				}
				.prose a:hover {
					color: #1e40af; /* Tailwind blue-800 */
					text-decoration: underline;
				}
				`}
            </style>

            {htmlContent ? (
              <div
                dangerouslySetInnerHTML={{
                  __html: htmlContent,
                }}
              />
            ) : (
              <p className="text-gray-500 italic">Loading...</p>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
